import ReactGA from "react-ga4";
import React from "react";

// Analytics utility functions for tracking user interactions
export const analytics = {
  // Track custom events
  trackEvent: (eventName, parameters = {}) => {
    ReactGA.event(eventName, parameters);
  },

  // Track feature usage
  trackFeatureUsage: (featureName, action = "used", additionalData = {}) => {
    ReactGA.event("feature_interaction", {
      feature_name: featureName,
      action: action,
      ...additionalData
    });
  },

  // Track chart interactions
  trackChartInteraction: (chartType, action, chartData = {}) => {
    ReactGA.event("chart_interaction", {
      chart_type: chartType,
      action: action,
      ...chartData
    });
  },

  // Track leaderboard interactions
  trackLeaderboardInteraction: (
    action,
    leaderboardType,
    additionalData = {}
  ) => {
    ReactGA.event("leaderboard_interaction", {
      action: action,
      leaderboard_type: leaderboardType,
      ...additionalData
    });
  },

  // Track search functionality
  trackSearch: (searchTerm, searchType = "general", resultsCount = null) => {
    ReactGA.event("search", {
      search_term: searchTerm,
      search_type: searchType,
      results_count: resultsCount
    });
  },

  // Track user engagement
  trackEngagement: (engagementType, details = {}) => {
    ReactGA.event("user_engagement", {
      engagement_type: engagementType,
      ...details
    });
  },

  // Track conversions/goals
  trackConversion: (conversionType, value = null, details = {}) => {
    ReactGA.event("conversion", {
      conversion_type: conversionType,
      value: value,
      ...details
    });
  },

  // Track errors
  trackError: (errorType, errorMessage, additionalData = {}) => {
    ReactGA.event("exception", {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
      ...additionalData
    });
  },

  // Track button clicks
  trackButtonClick: (buttonName, buttonLocation, additionalData = {}) => {
    ReactGA.event("button_click", {
      button_name: buttonName,
      button_location: buttonLocation,
      ...additionalData
    });
  },

  // Track form submissions
  trackFormSubmission: (formName, formType = "general", success = true) => {
    ReactGA.event("form_submission", {
      form_name: formName,
      form_type: formType,
      success: success
    });
  },

  // Track time spent on page/feature
  trackTimeSpent: (featureName, timeSpentSeconds, additionalData = {}) => {
    ReactGA.event("time_spent", {
      feature_name: featureName,
      time_spent_seconds: timeSpentSeconds,
      ...additionalData
    });
  },

  // Set user properties
  setUserProperty: (propertyName, value) => {
    ReactGA.set({ [propertyName]: value });
  },

  // Track scroll depth
  trackScrollDepth: (depth) => {
    ReactGA.event("scroll", {
      percent_scrolled: depth
    });
  }
};

// React hooks for common tracking patterns
export const useAnalytics = () => {
  return analytics;
};

// Hook for tracking component mount/unmount
export const useComponentTracking = (componentName) => {
  React.useEffect(() => {
    analytics.trackFeatureUsage(componentName, "viewed");

    return () => {
      // Track when component unmounts if needed
    };
  }, [componentName]);
};

// Hook for tracking time spent on a component
export const useTimeTracking = (componentName) => {
  const startTime = React.useRef(Date.now());

  React.useEffect(() => {
    return () => {
      const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
      analytics.trackTimeSpent(componentName, timeSpent);
    };
  }, [componentName]);
};
